<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store.js';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Draggable from 'vuedraggable';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const AUTOMATION_KEYS = [
  'auto_create_task_for_conversation',
  'auto_assign_task_to_agent',
  'sync_task_and_conversation_agents',
  'auto_resolve_conversation_on_task_end',
  'auto_complete_task_on_conversation_resolve',
  'sync_task_and_conversation_labels_priority',
];

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const boardId = computed(() => Number(route.params.boardId));
const currentAccountId = useMapGetter('getCurrentAccountId');
const board = computed(() =>
  store.getters['kanban/getBoardById'](boardId.value)
);
const steps = useMapGetter('kanban/getSteps');
const agents = useMapGetter('agents/getAgents');
const inboxes = useMapGetter('inboxes/getInboxes');

const form = ref({ name: '', description: '' });
const settings = ref({});
const localSteps = ref([]);
const newStep = ref({ name: '', color: '#3B82F6' });
const editingStepId = ref(null);
const isSaving = ref(false);

const sortedSteps = computed(() => steps.value);
const lastStepId = computed(() => {
  const nonCancelled = sortedSteps.value.filter(step => !step.cancelled);
  return nonCancelled[nonCancelled.length - 1]?.id;
});

const hydrate = () => {
  if (!board.value) return;
  form.value = {
    name: board.value.name,
    description: board.value.description,
  };
  settings.value = AUTOMATION_KEYS.reduce(
    (acc, key) => ({ ...acc, [key]: Boolean(board.value.settings?.[key]) }),
    {}
  );
};

const fetchData = () => {
  store.dispatch('kanban/fetchBoard', boardId.value);
  store.dispatch('agents/get');
  store.dispatch('inboxes/get');
};

onMounted(fetchData);

// Hidrata sempre que o board/etapas chegarem (robusto contra ordem de carga).
watch(board, hydrate, { immediate: true });
watch(
  sortedSteps,
  () => {
    localSteps.value = [...sortedSteps.value];
  },
  { immediate: true, deep: true }
);

const notifySaved = () => useAlert(t('KANBAN.SETTINGS.UPDATED_TOAST'));

const saveBasicInfo = async () => {
  isSaving.value = true;
  try {
    await store.dispatch('kanban/updateBoard', {
      boardId: boardId.value,
      name: form.value.name,
      description: form.value.description,
      settings: settings.value,
    });
    notifySaved();
  } catch (error) {
    useAlert(error.message);
  } finally {
    isSaving.value = false;
  }
};

const onToggleAutomation = async key => {
  try {
    await store.dispatch('kanban/updateBoard', {
      boardId: boardId.value,
      name: form.value.name,
      description: form.value.description,
      settings: { ...settings.value, [key]: settings.value[key] },
    });
    notifySaved();
  } catch (error) {
    settings.value[key] = !settings.value[key];
    useAlert(error.message);
  }
};

const toggleAgent = async agentId => {
  const current = board.value?.agent_ids || [];
  const next = current.includes(agentId)
    ? current.filter(id => id !== agentId)
    : [...current, agentId];
  try {
    await store.dispatch('kanban/updateBoardAgents', {
      boardId: boardId.value,
      agentIds: next,
    });
    notifySaved();
  } catch (error) {
    useAlert(error.message);
  }
};

const toggleInbox = async inboxId => {
  const current = board.value?.inbox_ids || [];
  const next = current.includes(inboxId)
    ? current.filter(id => id !== inboxId)
    : [...current, inboxId];
  try {
    await store.dispatch('kanban/updateBoardInboxes', {
      boardId: boardId.value,
      inboxIds: next,
    });
    notifySaved();
  } catch (error) {
    useAlert(error.message);
  }
};

const addStep = async () => {
  if (!newStep.value.name.trim()) return;
  try {
    await store.dispatch('kanban/createStep', {
      boardId: boardId.value,
      step: { name: newStep.value.name.trim(), color: newStep.value.color },
    });
    newStep.value = { name: '', color: '#3B82F6' };
    localSteps.value = [...sortedSteps.value];
    notifySaved();
  } catch (error) {
    useAlert(error.message);
  }
};

const updateStepField = async (step, field, value) => {
  try {
    await store.dispatch('kanban/updateStep', {
      boardId: boardId.value,
      stepId: step.id,
      step: { [field]: value },
    });
    localSteps.value = [...sortedSteps.value];
    notifySaved();
  } catch (error) {
    useAlert(error.message);
  }
};

const removeStep = async step => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('KANBAN.SETTINGS.REMOVE_STEP'))) return;
  try {
    await store.dispatch('kanban/deleteStep', {
      boardId: boardId.value,
      stepId: step.id,
    });
    localSteps.value = [...sortedSteps.value];
    notifySaved();
  } catch (error) {
    useAlert(error.message);
  }
};

const onStepsReorder = async () => {
  try {
    await Promise.all(
      localSteps.value.map((step, index) =>
        step.position === index
          ? Promise.resolve()
          : store.dispatch('kanban/updateStep', {
              boardId: boardId.value,
              stepId: step.id,
              step: { position: index },
            })
      )
    );
    notifySaved();
  } catch (error) {
    useAlert(error.message);
  }
};

const deleteBoard = async () => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('KANBAN.OVERVIEW.DELETE_CONFIRM'))) return;
  try {
    await store.dispatch('kanban/deleteBoard', boardId.value);
    router.push({
      name: 'kanban_view',
      params: { accountId: currentAccountId.value },
    });
  } catch (error) {
    useAlert(error.message);
  }
};

const goBack = () => {
  router.push({
    name: 'kanban_board',
    params: { accountId: currentAccountId.value, boardId: boardId.value },
  });
};
</script>

<template>
  <section class="w-full h-full overflow-y-auto bg-n-surface-1">
    <div class="w-full max-w-3xl px-8 py-6 mx-auto">
      <!-- Cabeçalho -->
      <div class="flex items-center justify-between mb-6">
        <div class="flex items-center gap-3 min-w-0">
          <Button ghost slate sm icon="i-lucide-arrow-left" @click="goBack" />
          <h1 class="text-lg font-medium truncate text-n-slate-12">
            {{ board?.name }}
          </h1>
          <span
            class="px-1.5 py-0.5 text-xs rounded bg-n-alpha-2 text-n-slate-11"
          >
            {{ board?.tasks_count }}
          </span>
        </div>
        <span class="text-xs text-n-slate-10">{{ `ID: ${boardId}` }}</span>
      </div>

      <!-- Informações básicas -->
      <div class="flex items-center justify-between mb-3">
        <h2 class="text-base font-medium text-n-slate-12">
          {{ t('KANBAN.SETTINGS.BASIC_INFO') }}
        </h2>
        <Button solid blue sm :is-loading="isSaving" @click="saveBasicInfo">
          {{ t('KANBAN.SETTINGS.SAVE_CHANGES') }}
        </Button>
      </div>
      <div class="flex flex-col gap-4 mb-8">
        <Input v-model="form.name" :label="t('KANBAN.SETTINGS.NAME_LABEL')" />
        <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
          {{ t('KANBAN.SETTINGS.DESCRIPTION_LABEL') }}
          <textarea
            v-model="form.description"
            rows="4"
            maxlength="2000"
            class="px-3 py-2 text-sm font-normal border rounded-lg outline-none resize-y bg-n-solid-1 border-n-weak text-n-slate-12"
          />
          <span class="self-end text-xs font-normal text-n-slate-10">
            {{ `${(form.description || '').length} / 2000` }}
          </span>
        </label>
      </div>

      <!-- Etapas -->
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-2">
          <h2 class="text-base font-medium text-n-slate-12">
            {{ t('KANBAN.SETTINGS.STEPS_TITLE') }}
          </h2>
          <span
            class="px-1.5 py-0.5 text-xs rounded bg-n-alpha-2 text-n-slate-11"
          >
            {{ localSteps.length }}
          </span>
        </div>
        <div class="flex items-center gap-2">
          <input
            v-model="newStep.color"
            type="color"
            class="flex-shrink-0 w-7 h-7 p-0 bg-transparent border-none rounded cursor-pointer"
          />
          <input
            v-model="newStep.name"
            :placeholder="t('KANBAN.SETTINGS.STEP_NAME_PLACEHOLDER')"
            class="px-2 py-1.5 text-sm border rounded-lg outline-none bg-n-solid-1 border-n-weak text-n-slate-12"
            @keyup.enter="addStep"
          />
          <Button faded slate sm icon="i-lucide-plus" @click="addStep">
            {{ t('KANBAN.SETTINGS.ADD_STEP') }}
          </Button>
        </div>
      </div>
      <Draggable
        :list="localSteps"
        item-key="id"
        handle=".step-drag-handle"
        class="flex flex-col gap-2 mb-8"
        ghost-class="opacity-50"
        @end="onStepsReorder"
      >
        <template #item="{ element: step }">
          <div
            class="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-n-solid-2"
          >
            <Icon
              icon="i-lucide-grip-vertical"
              class="flex-shrink-0 cursor-grab step-drag-handle size-4 text-n-slate-9"
            />
            <span
              class="flex-shrink-0 rounded-full size-2.5"
              :style="{ backgroundColor: step.color }"
            />
            <template v-if="editingStepId === step.id">
              <input
                :value="step.name"
                class="flex-1 min-w-0 px-2 py-1 text-sm border rounded outline-none bg-n-solid-1 border-n-weak text-n-slate-12"
                @keyup.enter="
                  updateStepField(step, 'name', $event.target.value);
                  editingStepId = null;
                "
                @blur="
                  updateStepField(step, 'name', $event.target.value);
                  editingStepId = null;
                "
              />
              <input
                type="color"
                :value="step.color"
                class="flex-shrink-0 w-6 h-6 p-0 bg-transparent border-none rounded cursor-pointer"
                @change="updateStepField(step, 'color', $event.target.value)"
              />
              <span
                class="flex-shrink-0 p-1 rounded hover:bg-n-alpha-1"
                role="button"
                :title="t('KANBAN.SETTINGS.REMOVE_STEP')"
                @click="removeStep(step)"
              >
                <Icon icon="i-lucide-trash-2" class="size-4 text-n-ruby-9" />
              </span>
            </template>
            <template v-else>
              <span class="flex-1 text-sm truncate text-n-slate-12">
                {{ step.name }}
              </span>
              <span
                v-if="step.tasks_count !== undefined"
                class="px-1.5 py-0.5 text-xs rounded bg-n-alpha-2 text-n-slate-11"
              >
                {{ step.tasks_count }}
              </span>
              <span
                v-if="step.id === lastStepId && !step.cancelled"
                class="text-xs font-medium text-n-teal-11"
              >
                {{ t('KANBAN.SETTINGS.WON_STEP') }}
              </span>
              <label
                v-else
                class="flex items-center gap-2 text-xs text-n-slate-11"
              >
                {{ t('KANBAN.SETTINGS.CANCELLED_STEP') }}
                <Switch
                  :model-value="step.cancelled"
                  @change="updateStepField(step, 'cancelled', !step.cancelled)"
                />
              </label>
              <span
                class="flex-shrink-0 p-1 rounded hover:bg-n-alpha-1"
                role="button"
                @click="editingStepId = step.id"
              >
                <Icon icon="i-lucide-pencil" class="size-4 text-n-slate-10" />
              </span>
            </template>
          </div>
        </template>
      </Draggable>

      <!-- Agentes -->
      <h2 class="mb-3 text-base font-medium text-n-slate-12">
        {{ t('KANBAN.SETTINGS.AGENTS_TITLE') }}
      </h2>
      <div class="flex flex-wrap gap-2 mb-8">
        <button
          v-for="agent in agents"
          :key="agent.id"
          type="button"
          class="px-3 py-1.5 text-sm border rounded-lg"
          :class="
            (board?.agent_ids || []).includes(agent.id)
              ? 'border-n-brand bg-n-solid-blue text-n-slate-12'
              : 'border-n-weak text-n-slate-11 hover:border-n-strong'
          "
          @click="toggleAgent(agent.id)"
        >
          {{ agent.name }}
        </button>
      </div>

      <!-- Caixas de entrada -->
      <h2 class="mb-3 text-base font-medium text-n-slate-12">
        {{ t('KANBAN.SETTINGS.INBOXES_TITLE') }}
      </h2>
      <div class="flex flex-wrap gap-2 mb-8">
        <button
          v-for="inbox in inboxes"
          :key="inbox.id"
          type="button"
          class="px-3 py-1.5 text-sm border rounded-lg"
          :class="
            (board?.inbox_ids || []).includes(inbox.id)
              ? 'border-n-brand bg-n-solid-blue text-n-slate-12'
              : 'border-n-weak text-n-slate-11 hover:border-n-strong'
          "
          @click="toggleInbox(inbox.id)"
        >
          {{ inbox.name }}
        </button>
      </div>

      <!-- Automação -->
      <h2 class="mb-1 text-base font-medium text-n-slate-12">
        {{ t('KANBAN.SETTINGS.AUTOMATION_TITLE') }}
      </h2>
      <p class="mb-3 text-sm text-n-slate-11">
        {{ t('KANBAN.SETTINGS.AUTOMATION_NOTE') }}
      </p>
      <div class="flex flex-col gap-4 mb-8">
        <div
          v-for="key in AUTOMATION_KEYS"
          :key="key"
          class="flex items-start gap-3"
        >
          <Switch
            v-model="settings[key]"
            class="mt-0.5"
            @change="onToggleAutomation(key)"
          />
          <div class="flex flex-col">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t(`KANBAN.SETTINGS.AUTOMATIONS.${key.toUpperCase()}`) }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ t(`KANBAN.SETTINGS.AUTOMATIONS.${key.toUpperCase()}_DESC`) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Excluir funil -->
      <h2 class="mb-1 text-base font-medium text-n-ruby-11">
        {{ t('KANBAN.SETTINGS.DELETE_TITLE') }}
      </h2>
      <p class="mb-3 text-sm text-n-slate-11">
        {{ t('KANBAN.SETTINGS.DELETE_WARNING') }}
      </p>
      <Button ruby sm icon="i-lucide-trash-2" class="mb-8" @click="deleteBoard">
        {{ t('KANBAN.SETTINGS.DELETE_BOARD') }}
      </Button>
    </div>
  </section>
</template>
